import { BaseComponent } from '../../base-component';
import { AddToBasketData, ArticleOptionsSelectedService, ArticleOptionsServices } from '../../global';
import { waitForCondition } from '../../_common/client/wait-for-condition';
import { ARTICLE_OPTIONS_CHANGED_EVENT } from '../../article-options/client/events';

export const ORDERING_COMPONENT_ROOT_SELECTOR = '.js_pdp_ordering';
export const ORDERING_COMPONENT_BUTTON_SELECTOR = '.js_pdp_ordering__button';
export const ORDERING_COMPONENT_QUANTITY_SELECTOR = '.js_pdp_ordering__quantity';

export class OrderingComponent extends BaseComponent {
    public readonly rootSelector = ORDERING_COMPONENT_ROOT_SELECTOR;

    private addToBasketBtnSelector = ORDERING_COMPONENT_BUTTON_SELECTOR;
    private quantitySelector = ORDERING_COMPONENT_QUANTITY_SELECTOR;
    private variationId: string | undefined;

    private selectedServices: ArticleOptionsSelectedService[] = [];

    public async init(): Promise<void> {
        await waitForCondition(() => !!window.o_order?.api);
        this.registerArticleOptionsSelectionChangedEvent();
    }

    public async onHtmlLoaded(): Promise<void> {
        this.resetSelectedServicesState();
        this.variationId = this.getRootElement().dataset?.variationId ?? '';
        this.registerAddToBasketClickEvent();
    }

    private getAddToBasketButtonElement(): HTMLElement {
        return this.getChildElement(this.addToBasketBtnSelector);
    }

    private getQuantityElement(): HTMLSelectElement {
        return document.querySelector(this.quantitySelector) as HTMLSelectElement;
    }

    private createAddToBasketData(): AddToBasketData {
        if (!this.variationId) {
            throw new Error('variationId is missing when creating add to basket data');
        }

        const trackingObject = this.createAddToBasketTrackingObject();
        return {
            variationId: this.variationId,
            quantity: this.getQuantityValue(),
            enteredArticleNumberAndPromotion: o_product.data.enteredArticleNumberAndPromotion,
            itemOrigin: 'ads',
            services: this.selectedServices,
            ...trackingObject,
        };
    }

    private createAddToBasketTrackingObject() {
        const availabilityTrackingDataJsonString = document.getElementById('availabilityTrackingData')?.textContent;
        const availabilityTrackingData = availabilityTrackingDataJsonString ? JSON.parse(availabilityTrackingDataJsonString) : {};

        const sustainabilityTrackingDataJsonString = document.getElementById('sustainabilityTrackingData')?.textContent;
        const sustainabilityTrackingData = sustainabilityTrackingDataJsonString ? JSON.parse(sustainabilityTrackingDataJsonString) : {};

        const offerScoreTrackingDataJsonString = document.getElementById('offerScoreTrackingData')?.textContent;
        const offerScoreTrackingData = offerScoreTrackingDataJsonString ? JSON.parse(offerScoreTrackingDataJsonString) : {};

        return {
            tracking: {
                ...availabilityTrackingData,
                ...sustainabilityTrackingData,
                ...offerScoreTrackingData,
            },
        };
    }

    private callAddToBasketApi(addToBasketData: AddToBasketData): void {
        o_order.api.addToBasket(addToBasketData);
    }

    private async handleClickEvent(mouseEvent: MouseEvent): Promise<void> {
        mouseEvent.preventDefault();
        mouseEvent.stopPropagation();

        const addToBasketData = this.createAddToBasketData();
        this.callAddToBasketApi(addToBasketData);
    }

    private getQuantityValue(): string {
        const quantityElement = this.getQuantityElement();
        return quantityElement ? quantityElement.options[quantityElement.selectedIndex].value : '1';
    }

    private registerAddToBasketClickEvent(): void {
        this.getAddToBasketButtonElement().addEventListener('click', async e => await this.handleClickEvent(e), false);
    }

    private registerArticleOptionsSelectionChangedEvent(): void {
        o_global.eventQBus.on(ARTICLE_OPTIONS_CHANGED_EVENT, (data: ArticleOptionsServices) => {
            this.selectedServices = data.services;
        });
    }

    private resetSelectedServicesState(): void {
        this.selectedServices = [];
    }
}
